import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import ImageUpload from '@/components/ImageUpload';

const CATEGORY_OPTIONS = [
  { value: 'fashion', label: '패션' }, { value: 'beauty', label: '뷰티' },
  { value: 'food', label: '푸드' }, { value: 'kids', label: '키즈' },
  { value: 'pet', label: '반려동물' }, { value: 'electronics', label: '전자제품' },
  { value: 'home', label: '홈' }, { value: 'sports', label: '스포츠' },
  { value: 'others', label: '기타' },
];

const PLATFORM_OPTIONS = [
  { value: 'youtube', label: 'YouTube' }, { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' }, { value: 'naver', label: 'Naver Shopping Live' },
  { value: 'kakao', label: 'Kakao Shopping Live' }, { value: 'grip', label: 'Grip' },
  { value: 'others', label: '기타' },
];

const StatusBanner = ({ status, application }) => {
  if (status === 'pending') {
    return (
      <div className="flex gap-3 p-5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20" data-testid="apply-status-pending">
        <Clock className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-bold text-amber-800 dark:text-amber-300">심사 중입니다</p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">관리자가 검토 후 결과를 알려드립니다. 검토 중에는 새로운 신청서를 제출할 수 없습니다.</p>
        </div>
      </div>
    );
  }
  if (status === 'approved') {
    return (
      <div className="flex gap-3 p-5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20" data-testid="apply-status-approved">
        <CheckCircle2 className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <p className="font-bold text-emerald-800 dark:text-emerald-300">판매자 승인이 완료되었습니다</p>
          <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">판매자 대시보드에서 방송과 상품을 관리할 수 있습니다.</p>
          <Link to="/seller/dashboard" className="inline-block mt-3 px-4 py-2 bg-[#FF3B30] text-white text-xs font-bold rounded-full" data-testid="apply-goto-dashboard">
            판매자 대시보드로 이동
          </Link>
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex gap-3 p-5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20" data-testid="apply-status-rejected">
        <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-bold text-red-800 dark:text-red-300">신청이 반려되었습니다</p>
          <p className="text-xs text-red-700 dark:text-red-400 mt-1">사유: {application?.rejection_reason || '관리자 메모 없음'}</p>
          <p className="text-xs text-red-700 dark:text-red-400 mt-1">아래 양식을 보완하여 다시 신청해주세요.</p>
        </div>
      </div>
    );
  }
  if (status === 'suspended') {
    return (
      <div className="flex gap-3 p-5 rounded-lg bg-gray-100 dark:bg-white/5 border border-black/10 dark:border-white/10" data-testid="apply-status-suspended">
        <AlertTriangle className="text-gray-600 dark:text-gray-300 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-bold">판매자 이용이 제한되었습니다</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">관리자 문의를 통해 이용 재개 여부를 확인해주세요.</p>
        </div>
      </div>
    );
  }
  return null;
};

const SellerApply = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sellerStatus, setSellerStatus] = useState('none');
  const [application, setApplication] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    seller_name: '', representative_name: '', phone: '',
    contact_email: user?.email || '', primary_category: 'fashion',
    platforms: [], platform_urls: {},
    introduction: '', application_reason: '',
    profile_image_url: '',
    business_name: '', business_registration_number: '',
    mail_order_registration_number: '', business_document_url: '',
    monthly_broadcast_count: '', target_audience: '', additional_notes: '',
    agree_privacy: false, agree_policy: false,
  });

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/seller/application/me');
      setSellerStatus(res.data.seller_status || 'none');
      setApplication(res.data.application || null);
      if (res.data.application) {
        setForm((f) => ({
          ...f,
          seller_name: res.data.application.seller_name || '',
          representative_name: res.data.application.representative_name || '',
          phone: res.data.application.phone || '',
          contact_email: res.data.application.contact_email || user?.email || '',
          primary_category: res.data.application.primary_category || 'fashion',
          platforms: res.data.application.platforms || [],
          platform_urls: res.data.application.platform_urls || {},
          introduction: res.data.application.introduction || '',
          application_reason: res.data.application.application_reason || '',
          profile_image_url: res.data.application.profile_image_url || '',
          business_name: res.data.application.business_name || '',
          business_registration_number: res.data.application.business_registration_number || '',
          mail_order_registration_number: res.data.application.mail_order_registration_number || '',
          business_document_url: res.data.application.business_document_url || '',
          monthly_broadcast_count: res.data.application.monthly_broadcast_count || '',
          target_audience: res.data.application.target_audience || '',
          additional_notes: res.data.application.additional_notes || '',
        }));
      }
    } catch (e) {
      setError('신청 상태를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const togglePlatform = (p) => {
    setForm((f) => f.platforms.includes(p)
      ? { ...f, platforms: f.platforms.filter((x) => x !== p) }
      : { ...f, platforms: [...f.platforms, p] });
  };

  const setPlatformUrl = (p, url) => setForm((f) => ({ ...f, platform_urls: { ...f.platform_urls, [p]: url } }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.platforms.length === 0) { setError('활동 플랫폼을 1개 이상 선택해주세요.'); return; }
    if (!form.agree_privacy || !form.agree_policy) { setError('필수 동의 항목에 동의해주세요.'); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const endpoint = sellerStatus === 'rejected' ? '/seller/application/resubmit' : '/seller/application';
      await api.post(endpoint, form);
      setSuccess('판매자 신청이 정상적으로 접수되었습니다.');
      toast.success('판매자 신청이 접수되었습니다.');
      await loadStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || '저장에 실패했습니다. 다시 시도해주세요.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0A0A0A]"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]" /></div>;
  }

  const editable = sellerStatus === 'none' || sellerStatus === 'rejected';
  const showApproved = sellerStatus === 'approved';

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-8">
        <header data-testid="apply-header">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">
            <ShieldCheck size={14} /> LIVEMOA 판매자 신청
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            판매자로 활동해보세요
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            신청서를 제출하면 관리자가 검토 후 승인 여부를 알려드립니다. 승인 후에는 같은 계정으로 소비자 기능과 판매자 기능을 모두 사용할 수 있습니다.
          </p>
        </header>

        <StatusBanner status={sellerStatus} application={application} />

        {showApproved && (
          <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg p-6" data-testid="apply-approved-summary">
            <p className="text-sm font-bold mb-2">신청서 요약</p>
            <div className="text-sm text-gray-700 dark:text-gray-300 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
              <div><span className="text-gray-500">셀러명</span> · {application?.seller_name}</div>
              <div><span className="text-gray-500">대표자</span> · {application?.representative_name}</div>
              <div><span className="text-gray-500">대표 카테고리</span> · {application?.primary_category}</div>
              <div><span className="text-gray-500">활동 플랫폼</span> · {(application?.platforms || []).join(', ')}</div>
            </div>
          </div>
        )}

        {!showApproved && sellerStatus !== 'suspended' && (
          <form onSubmit={submit} className="space-y-8 bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg p-6" data-testid="apply-form">
            {error && <div className="p-3 rounded bg-[#FF3B30]/10 text-[#FF3B30] text-sm font-medium" data-testid="apply-error">{error}</div>}
            {success && <div className="p-3 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-sm font-medium" data-testid="apply-success">{success}</div>}

            <section>
              <h2 className="font-bold mb-3 text-base">기본 정보</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder="셀러명 / 채널명 *" value={form.seller_name} onChange={(e) => setForm({ ...form, seller_name: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-seller-name" disabled={!editable} />
                <input required placeholder="대표자 이름 *" value={form.representative_name} onChange={(e) => setForm({ ...form, representative_name: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-rep-name" disabled={!editable} />
                <input required placeholder="연락처 (010-xxxx-xxxx) *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-phone" disabled={!editable} />
                <input required type="email" placeholder="연락용 이메일 *" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-email" disabled={!editable} />
                <select value={form.primary_category} onChange={(e) => setForm({ ...form, primary_category: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-category" disabled={!editable}>
                  {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </section>

            <section>
              <h2 className="font-bold mb-3 text-base">활동 플랫폼 *</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3" data-testid="apply-platforms">
                {PLATFORM_OPTIONS.map((p) => {
                  const active = form.platforms.includes(p.value);
                  return (
                    <button type="button" key={p.value} disabled={!editable} onClick={() => togglePlatform(p.value)} data-testid={`apply-platform-${p.value}`}
                      className={`px-3 py-2 rounded-full text-xs font-bold border ${active ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10'}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {form.platforms.map((p) => (
                <input key={p} disabled={!editable} placeholder={`${PLATFORM_OPTIONS.find((x) => x.value === p)?.label || p} 프로필/채널 URL`} value={form.platform_urls[p] || ''} onChange={(e) => setPlatformUrl(p, e.target.value)} className="w-full mb-2 px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid={`apply-url-${p}`} />
              ))}
            </section>

            <section>
              <h2 className="font-bold mb-3 text-base">소개</h2>
              <textarea required rows={3} disabled={!editable} placeholder="셀러/브랜드 소개 *" value={form.introduction} onChange={(e) => setForm({ ...form, introduction: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-intro" />
              <textarea required rows={2} disabled={!editable} placeholder="LIVEMOA 판매자 신청 사유 *" value={form.application_reason} onChange={(e) => setForm({ ...form, application_reason: e.target.value })} className="w-full mt-2 px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-reason" />
            </section>

            <section>
              <h2 className="font-bold mb-3 text-base">이미지</h2>
              <ImageUpload
                value={form.profile_image_url}
                onChange={(url) => setForm({ ...form, profile_image_url: url })}
                kind="seller_profile" label="판매자 프로필 이미지" aspectClass="aspect-square"
                testId="apply-profile-img"
              />
            </section>

            <section>
              <h2 className="font-bold mb-3 text-base">사업자 정보 (선택)</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input disabled={!editable} placeholder="상호명" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-business-name" />
                <input disabled={!editable} placeholder="사업자등록번호" value={form.business_registration_number} onChange={(e) => setForm({ ...form, business_registration_number: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-brn" />
                <input disabled={!editable} placeholder="통신판매업 신고번호" value={form.mail_order_registration_number} onChange={(e) => setForm({ ...form, mail_order_registration_number: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-mail-order" />
              </div>
              <div className="mt-3">
                <ImageUpload value={form.business_document_url} onChange={(url) => setForm({ ...form, business_document_url: url })} kind="business_document" label="사업자 등록증 (JPG/PNG/PDF, 5MB 이내)" aspectClass="aspect-video" testId="apply-business-doc" />
                <p className="text-[11px] text-gray-500 mt-1">사업자 관련 문서는 관리자 심사용으로 안전하게 저장되며, 일반 사용자에게 공개되지 않습니다.</p>
              </div>
            </section>

            <section>
              <h2 className="font-bold mb-3 text-base">추가 정보 (선택)</h2>
              <div className="space-y-2">
                <input disabled={!editable} placeholder="월 예상 방송 횟수" value={form.monthly_broadcast_count} onChange={(e) => setForm({ ...form, monthly_broadcast_count: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-monthly-count" />
                <input disabled={!editable} placeholder="주요 타깃 고객층" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-target" />
                <textarea disabled={!editable} rows={2} placeholder="기타 안내 사항" value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="apply-notes" />
              </div>
            </section>

            <section className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" disabled={!editable} checked={form.agree_privacy} onChange={(e) => setForm({ ...form, agree_privacy: e.target.checked })} className="accent-[#FF3B30]" data-testid="apply-agree-privacy" />
                개인정보 수집·이용에 동의합니다 (필수)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" disabled={!editable} checked={form.agree_policy} onChange={(e) => setForm({ ...form, agree_policy: e.target.checked })} className="accent-[#FF3B30]" data-testid="apply-agree-policy" />
                LIVEMOA 판매자 운영 정책에 동의합니다 (필수)
              </label>
            </section>

            {editable && (
              <button type="submit" disabled={submitting} className="w-full py-3 bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-50 text-white font-bold rounded-full" data-testid="apply-submit">
                {submitting ? '접수 중...' : (sellerStatus === 'rejected' ? '재신청 접수' : '판매자 신청 접수')}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default SellerApply;
